/*
    Copyright (C) Paul B. Lucas 2025, 2026 ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that uses various typology configs to attempt to 
    infer various asset properties (aset-type, mfg, model) from the provided description
    
    Methods incuded:

        fixupAssetProps()
    

    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Imports
    can do this from a basic JSON file.  Parameters in the config array include the basic unit and typology 
    definitions.

    
    <Revision History>
        04/15/2025      pblucas     created 
        03/16/2026      pblucas     Corrected typos & updated copyright

    </Revision History>
*/

import { ref }          from 'vue'  

import useArchiBusAPI   from '@/composables/useArchiBusAPI'     

import Typology         from '@/configuration/assetTypology.json'
import ModelMap         from '@/configuration/mfgModelMap.json'
import MfgDefaults      from '@/configuration/mfgDefaults.json'

const useInferenceAPI = () =>    {

    const {  employeeEMResults, fetchEmplDataOnLegalName } = useArchiBusAPI()

    // set typology config arrays
    const typology      =   ref([])
    typology.value      =   Typology['asset-typology']

    // unit definitions
    const units         =   ref([])
    units.value         =   Typology['units']

    const mfgModelMap   =   ref([])
    mfgModelMap.value   =   ModelMap

    const mfgDefaults   =   ref([])
    mfgDefaults.value   =   MfgDefaults

    const finExcissions =   ref([])
    finExcissions.value =   Typology['financial-excissions']

   const failList       =   ref([])   
   
   
    // result reference to return array of modified asset property arrays 
    const assetProps            =   ref([]) 
    const bBadFile              =   ref(false)

    // added to return location of regex errors in MfgModelMap to calling routine
    const oRegexError           =   ref({
        "status":       false,
        "error":        "",
        "mfg-row":      null,
        "model-row":    null
    })

    // FUNCTION: accepts an asset property array and runs inference engine
    // to creates a modified assetProp array consistent with asset database
    //
    // Used to import and convert FabWeb+ and FabWeb asset property semantics into
    // those used in the system
    //
    // Arguments:
    //      importedProps       array of asset properties to modify
    //      source              string designator of source for data (FABWEB+, FABWEB, THIS)
    //      failList            List of failed graph employees to avoid repreated calls.

    // Note:  function is async so as to be able to make and await graph calls for netid in steward name
    const  fixupAssetProps =  async (importedProps, source, stats)  =>  {

        // for production, this should be set to false
        // this allows all assets inported to be set to Active-Pending-Issue regaradless of custodian of record
        let ISSUE_TESTING = true

        // set some Boolean switches
        var bTypeInferenceComplete  =   false
        var bMfgInferenceComplete   =   false
        var bModelInferenceComplete =   false
        var bHasCustodian           =   false
        failList.value              =   []

        // clear return value
        assetProps.value = []

        // MARK: - IMPORT PROCESS DIFFERENTIATION
        //
        // FabWeb+ process is determined with source == 'FABWEB+'
        // FabWeb process is with source == FABWEB
        // Other Excel imports is set with processSelector == 'OTHER'
        //
        switch(source) {
            case 'FABWEB+':

                /*   
                FABWeb+ 'General Reports' is a multisheet Excel XLSX export with each sheet being a separate org code/title
                Note that this is a consolidated report containing both FabWeb and FabWeb+ unique data
                Row 5 is the 'title' and is formatted as 1-ORG-Name, e.g., '1-618-Ofc of the Chief Info Officer'
                Row 6 contains the column headers
                Row 7 is the start of data

                Row 6 column assignments for FabWeb+ data
                    [
                        "Org Code"              -->  6-digit org code, e.g. 618000, 798000
                        "Item ID"               -->  tag
                        "Fab Web Otag"          -->  import of Otag from FabWeb
                        "Description"           -->  description -  parsed for mfg/model/type
                        "Is Fab Web Item"       -->  Boolean - item pulled from FabWeb
                        "Category"              -->  asset-type blank | type
                                                     PC Desktop, PC Laptop, Macbook, Printer, Imac, Tablet, Monitor,
                                                     Cell Phone, Server, Switch, Racks, AV Equipment, Camera, Dispose,
                                                     Docking Station, Primary Macbook, Primary PC Laptop, Projector
                        "Keywords"              -->  not mapped
                        "Custodian"             -->  steward
                        "Custodian UIN"         -->  steward UIN                                             
                        "Location"              -->  location-code
                        "Location Description"  -->  location detail
                        "Manufacturer"          -->  mfg
                        "Model"                 -->  model
                        "Serial Number"         -->  serial number
                        "Invoice Number"        -->  invoice of original purchase
                        "Cost"                  -->  purchase price
                        "Purchase Date"         -->  purchase date
                        "Active"                -->  Boolean
                        "Disposal Method"       -->  not mapped
                        "Disposal Date"         -->  not mapped
                        "Loan Form Reqd"        -->  mapped to status
                        "Loan Form Active"      -->  mapped into status
                    ]

                    NOTE:  FABWEB+ DATA FORMATS CHANGE WITH ORG
                    The first sheet has a different structure than the subsequent sheets. One additional null row.  This
                    affects the header row (_HDR_ROW) and start of data rows (_START_OF_DATA).
                    For first sheet the values are 5/6, for subsequent sheets, 4/6.
                    
                    To determine which we test which row has first column with string "Org Code" which is the header row indicator.
                */

                // iterate over first few rows to determine actual format
                // Note: spreadsheet counts are cardinal not indexing so adjust indicies accordingly
                let _HDR_ROW        =   0
                let _START_OF_DATA  =   0
                let _END_OF_DATA    =   0
                let col_offset      =   0
                let bAligned        =   false
                const regexkey = /Org[ ]?Code/i
                for (let tr = 0; (tr < 6) && !bAligned; tr++)  {
                    for (let j = 0; (j < 6) && !bAligned; j++) {
                        // attempt to deduce column offset
                        if(importedProps[tr]?.[j] != "") {
                             if(importedProps[tr]?.[j].match(regexkey)) {
                                bAligned = true     // terminate search
                                col_offset = j
                                _HDR_ROW        =   tr
                                _START_OF_DATA  =   tr+1    
                                _END_OF_DATA = importedProps.length  
                            }                             
                        }
                    }
                }
                if(_HDR_ROW == 0 || _START_OF_DATA == 0) {
                    bBadFile.value = true
                    return;
                }

                // DEBUG
                //console.log("useInferenceAPI:: HDR_ROW: "+_HDR_ROW+", START_OF_DATA: "+_START_OF_DATA+", END_OF_DATA: "+(_END_OF_DATA-1)+", COL OFFSET: "+col_offset)
                //console.log("useInferenceAPI:: N\ote: Blank rows are supressed in AOA")
                // row indexing

                // header key indexing
                let _NULL_1         =   0
                let _ORGCODE        =   0  + col_offset
                let _ITEM_ID        =   1  + col_offset
                let _NULL_2         =   2  + col_offset
                let _NULL_3         =   3  + col_offset
                let _FABWEB_OTAG    =   4  + col_offset
                let _DESCRIPTION    =   5  + col_offset
                let _IS_FABWEB      =   6  + col_offset
                let _NULL_4         =   7  + col_offset
                let _CATEGORY       =   8  + col_offset
                let _KEYWORDS       =   9  + col_offset
                let _CUSTODIAN      =   10 + col_offset
                let _CUSTODIAN_UIN  =   11 + col_offset
                let _LOCATION       =   12 + col_offset     
                let _LOC_DETAIL     =   13 + col_offset
                let _MFG            =   14 + col_offset
                let _MODEL          =   15 + col_offset
                let _SERIAL_NO      =   16 + col_offset
                let _INVOICE_NO     =   17 + col_offset
                let _COST           =   18 + col_offset
                let _PURCHASE_DATE  =   19 + col_offset
                let _ACTIVE         =   20 + col_offset
                let _DISP_METHOD    =   21 + col_offset
                let _DISP_DATE      =   22 + col_offset
                let _LOAN_REQD      =   23 + col_offset
                let _LOAN_ACTIVE    =   24 + col_offset
  
                // check to see if row 0 conforms to the above structure
                if((importedProps[_HDR_ROW]?.[_ORGCODE] ?? '').toLowerCase() != 'org code')                  bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_ITEM_ID] ?? '').toLowerCase() != 'item id')                   bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_FABWEB_OTAG] ?? '').toLowerCase() != 'fab web otag')          bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_DESCRIPTION] ?? '').toLowerCase() != 'description')           bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_IS_FABWEB] ?? '').toLowerCase() != 'is fab web item')         bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_CATEGORY] ?? '').toLowerCase() != 'category')                 bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_KEYWORDS] ?? '').toLowerCase() != 'keywords')                 bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_CUSTODIAN] ?? '').toLowerCase() != 'custodian')               bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_CUSTODIAN_UIN] ?? '').toLowerCase() != 'custodian uin')       bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_LOCATION] ?? '').toLowerCase() != 'location')                 bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_LOC_DETAIL] ?? '').toLowerCase() != 'location description')   bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_MFG] ?? '').toLowerCase() != 'manufacturer')                  bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_MODEL] ?? '').toLowerCase() != 'model')                       bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_SERIAL_NO] ?? '').toLowerCase() != 'serial number')           bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_INVOICE_NO] ?? '').toLowerCase() != 'invoice number')         bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_COST] ?? '').toLowerCase() != 'cost')                         bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_PURCHASE_DATE] ?? '').toLowerCase() != 'purchase date')       bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_ACTIVE] ?? '').toLowerCase() != 'active')                     bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_DISP_METHOD] ?? '').toLowerCase() != 'disposal method')       bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_DISP_DATE] ?? '').toLowerCase() != 'disposal date')           bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_LOAN_REQD] ?? '').toLowerCase() != 'loan form reqd')          bBadFile.value = true
                if((importedProps[_HDR_ROW]?.[_LOAN_ACTIVE] ?? '').toLowerCase() != 'loan form active')      bBadFile.value = true

                 if( bBadFile.value == false) {
                    // in FABWEB+ format, so proceed

                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    //
                    //      LOOP OVER DATA INPUT ROWS
                    //
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    ridxLoop: for( let ridx = _START_OF_DATA; ridx < _END_OF_DATA; ridx++) {

                        // asset property storage object (to be written to database)
                        let aEachAssetPropSet   =   {}
                                                
                        // starting conditions
                        bMfgInferenceComplete   =   false
                        bModelInferenceComplete =   false
                        bTypeInferenceComplete  =   false
                        let assetClassifier     =   ""
                        stats.inputRecords++
 
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      Set owning org  Check to see if in proper format
                        //      Note: Some orgcodes are in spreadsheet as integer
                        //
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        try {
                            let checkCode = (importedProps[ridx]?.[_ORGCODE]).toString()
                            if( checkCode.length != 6) throw new Error("orgcode not in proper 6 digit format")
                            aEachAssetPropSet['owner-org'] = checkCode.substring(0,3)
                        }
                        catch(e) {
                            console.log ("useInferenceAPI:: Data error: "+e)
                            // skip this record
                            stats.badOrgcodeRecords++
                            continue
                        }
                        
                        
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      GENERAL PROCESS FOR MANIPULATION OF DATA ELEMENTS
                        //
                        //      Uses asset-typology:
                        //          'atype'             asset-type value
                        //          'dep-category'      depreciation term
                        //          'audit-cat'         State audit category
                        //          'aliases'           Array of strings used to infer type
                        //
                        //      1. Sanitize _DESC field for special characters that will fubar SQL insert.  Sanitize
                        //         _ITEM_ID for proper format.  Improper tags are rewritten to 12 char plus 'truncated'
                        //         to preserve data.
                        //      2. Copy pass thru props that don't depend on type/mfg/model/description
                        //      3. Attempt to infer asset type from mfg, model, or description.  On regex matches with
                        //         model number, set asset classifier
                        //      4. If cannot parse model from _MODEL field attempt to do so on _DESC field. Set asset classifier
                        //      5. If asset classifier is not set (== 'unresolved'), attempt to determine classifier from _CATEGORY field.
                        //         Note:  Many _CATEGORY fields are blank ("").  Skip function if blank.
                        //      6. If asset classifier is set (!= 'unresolved'), use classifier to look up on aAssetTopology structure
                        //         and set asset-type, audit-cat, and dep(reciation)-category.
                        //      6a. If not set, then use defaults
                        //      7. If 'custodian' is set, determine whether custodian is listed as unit 
                        //         steward, and if so set 'steward' to unit 'short-name' and 'steward-netid'
                        //         to 'unit-id'
                        //      8. If 'custodian' is not in unit-steward list, set 'steward' to 'custodian'band
                        //         perform graph lookup on'custodian' to extract netid.
                        //      9. If mfg or models are not determined from process, prefix with * and copy data as 
                        //         specified on input
                        //      10. Store prop array as asset and reiterate next row  
                        //
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                        // sanitize the description e.g. '20" Monitors' so as to allow SQL store to work
                        let sanDesc = (importedProps[ridx]?.[_DESCRIPTION] ??' ').replace(/["]/g, '\\"')
                        aEachAssetPropSet['description']        =   sanDesc

                        // Eliminate any accounting records
                        let finRegex = {}
                        for(let f = 0; f < finExcissions.value.length; f++) {
                            try {
                                finRegex = new RegExp(finExcissions.value[f], 'i')
                                // continues only if valid regex otherwise catch block executed
                                if(finRegex.test(sanDesc)) {
                                    // regex match
                                    stats.finRecords++
                                    // ignore record and restart input record processing
                                    continue ridxLoop
                                } 
                            }
                            catch(e) {
                                console.log("Bad Regex in finExcissions on indicies i,j: "+f)
                                oRegexError.value.status        = true
                                oRegexError.value.error         = "regex error in financial recissions map"
                                oRegexError.value['mfg-row']    = f
                            }               
                        }
                        

                        // sanitize and test _ITEM_ID.  Some ids are abnormal.  Database has field length 32 chars
                        // and in excess will throw errors.  Limit to 12 plus words 'truncated' and continue
                        try {
                            let tagLength = importedProps[ridx]?.[_ITEM_ID]?.length ?? "tag undef"
                            if(tagLength > 32 || typeof tagLength == 'undefined') throw Error("tag length: "+tagLength)
                            aEachAssetPropSet['tag']                =   importedProps[ridx][_ITEM_ID]
                        }
                        catch(e) {
                            console.log("useInferenceAPI:: tag length test: "+e)
                            stats.badItemIDLengthRecords++
                            aEachAssetPropSet['tag'] =   importedProps[ridx][_ITEM_ID].substring(0,12)+"_truncated"
                        }

                        // pass thrus - these props do not depend on asset-type, mfg, or model                       
                        aEachAssetPropSet['otag']               =   importedProps[ridx][_FABWEB_OTAG]
                        aEachAssetPropSet['location']           =   importedProps[ridx][_LOCATION]
                        aEachAssetPropSet['location-detail']    =   importedProps[ridx][_LOC_DETAIL]
                        aEachAssetPropSet['serial-no']          =   importedProps[ridx][_SERIAL_NO]
                        aEachAssetPropSet['invoice-number']     =   importedProps[ridx][_INVOICE_NO]
                        aEachAssetPropSet['purchase-date']      =   importedProps[ridx][_PURCHASE_DATE]
                        
                        // if cost is null, reported as "" on import, reset to 0.00 float to satisfy SQL
                        // database table description (cost specified as float) to avoid insert error
                        if(importedProps[ridx][_COST] == "")    aEachAssetPropSet['cost']  =   0.00
                        else                                    aEachAssetPropSet['cost']  =   importedProps[ridx][_COST]

                        // Check Boolean for data record source (only on FabWeb Plus)
                        // FABWeb+ Boolean for whether the item is from FabWeb or entered directly in +
                        if(importedProps[ridx][_IS_FABWEB]) {   
                            aEachAssetPropSet['source-code']  =   'FABWEB'
                        }
                        else {
                            aEachAssetPropSet['source-code']  =   'FABWEB+'
                        }
                      
                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      INFERENCE PROCESS FOR DETERMINING MANUFACTURER/MODEL 
                        //
                        //      Attempt to match up a model.  The first index iterates the map records and the
                        //      the second index (j) iterates the model regex.  If the model inference fails on 
                        //      the first index, apply the regex to the manufacturer field for a partial solution. 
                        //      This will not give an asset type.
                        // 
                        //      Note:  I added the try/catch block to the regex generation because the mfgModelMap
                        //             is a constant source of errors (regex errors as items are added).   The catch block
                        //             will emit a console log on the map indices and also pass a ref value back to the 
                        //             calling routine.   This allows the calling routine to flag the errors for the user.
                        //
                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                        // guard against null prop on imported data on _MFG and _MODEL columns
                        let candidateMfg    = (importedProps[ridx]?.[_MFG] ?? "na").toLowerCase().trim()
                        let candidateModel  = (importedProps[ridx]?.[_MODEL] ?? "na").toLowerCase().trim()
                        
                        //  some model entries are blank or marked N/A or NA - speed up by avoiding these iterations
                        if(     (candidateModel.length == 0)
                            ||   candidateModel.toLowerCase().includes('n/a')
                            ||   candidateModel.toLowerCase().includes('na') ) {
                            
                            stats.modelInputEmpty++
                            // drop through with inference incomplete to description inference.
                        }
                        else  {
                        // iterate over map (src/configuration/mfgModelMap.json)
                            mmInference: for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) { 
                                for( let j = 0; (j < mfgModelMap.value['mm-map'][i].models.length); j++) {               

                                    let modelRegex = {}
                                    try {
                                        modelRegex = new RegExp(mfgModelMap.value['mm-map'][i]['models'][j].regex, 'i')
                                        // continues only if valid regex otherwise catch block executed
                                        if(modelRegex.test(candidateModel)) {
                                            // regex match
                                            aEachAssetPropSet['model']  =   mfgModelMap.value['mm-map'][i].models[j].cmm
                                            aEachAssetPropSet['mfg']    =   mfgModelMap.value['mm-map'][i].cn
                                            assetClassifier             =   mfgModelMap.value['mm-map'][i].models[j].classifier   
                                            bModelInferenceComplete     =   true                                             
                                            bMfgInferenceComplete       =   true      
                                            stats.resolvedModelInference++
                                            // break iteration inner and outer since we have a solution
                                            break mmInference
                                        } 
                                    }
                                    catch(e) {
                                        console.log("Bad Regex in mfgModelMap (MM) on indicies i,j: "+i+", "+j)
                                        oRegexError.value.status        = true
                                        oRegexError.value.error         = "regex error in model map"
                                        oRegexError.value['mfg-row']    = i
                                        oRegexError.value['model-row']  = j
                                    }                                
                                }   // end model iteration     
                            }       // end on mmap record mfg iteration
                        }

  
                        // if model or mfg not found (inferenceComplete)
                        // attempt inference on description
                        if(!bMfgInferenceComplete || !bModelInferenceComplete)    {

                            let aDesc  = (importedProps[ridx]?.[_DESCRIPTION] ?? "").toLowerCase().trim()
                            
                            // apply regex to  description
                            mapOLoop: for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) {   
                                for( let j =0; (j < mfgModelMap.value['mm-map'][i].models.length); j++) {
                                    
                                    let descRegex = {}
                                    // try/catch to catch possibly bad regex in maps
                                    try {
                                        descRegex = new RegExp(mfgModelMap.value['mm-map'][i]['models'][j].regex, 'i')
 
                                        if(descRegex.test(aDesc)) {
                                            // regex matched model in description
                                            aEachAssetPropSet['mfg']    =  mfgModelMap.value['mm-map'][i].cn 
                                            aEachAssetPropSet['model']  =  mfgModelMap.value['mm-map'][i].models[j].cmm       
                                            assetClassifier             =  mfgModelMap.value['mm-map'][i].models[j].classifier 
                                            bModelInferenceComplete     =   true                                         
                                            bMfgInferenceComplete       =   true 
                                            
                                            // exit as we have a solution
                                            break mapOLoop
                                        }   
                                    }
                                    catch(e) {
                                        console.log("Bad Regex in mfgModelMap (DESC) on indicies i,j: "+i+", "+j)
                                        oRegexError.value.status        = true
                                        oRegexError.value.error         = "regex error in model map"
                                        oRegexError.value['mfg-row']    = i
                                        oRegexError.value['model-row']  = j
                                    }                                                                               
                                }    
                            }   
                        }
                                               
                        // consider possibility that the model is unknown but the manufacturer listed is approximately
                        // valid.   Iterate manufacturers on map and if matched set to cannonical mfg name
                        // Since there is no model, can't resolve asset classifier
                        if (!bMfgInferenceComplete) {
                            for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) { 

                                let mfgRegex = {}
                                try {
                                    mfgRegex = new RegExp(mfgModelMap.value['mm-map'][i]?.regex?.replaceAll('/',''), 'i')
                                    if(mfgRegex.test(candidateMfg)) {
                                        // match on mfg regex
                                        aEachAssetPropSet['mfg']    =   mfgModelMap.value['mm-map'][i].cn
                                        bMfgInferenceComplete       =   true
                                        break
                                    }
                                }
                                catch(e) {
                                    console.log("Bad Regex in mfgModelMap (MFG) on index: i: "+i)
                                    oRegexError.value.status        = true
                                    oRegexError.value.error         = "regex error in mfg map"
                                    oRegexError.value['mfg-row']    = i
                                    oRegexError.value['model-row']  =   null
                                }
                            }   // end map loop on mfg
                        }

                        // set up asset type based on assetClassifier
                        // - if the classifier has been set (len > 0) then use the classifier to index typology
                        //   aliases to obtain the asset-type.
                        // - if the classifier is not set, attempt to parse the _CATEGORY field.
                        //   This field is poorly maintained and can be blank ("")
                        //   match against typology aliases to get asset-type from atype key
                        // - if this fails, deault the type to unknown.
                        //
                        if (assetClassifier !== "" )     {

                            // have classifier so lookup typology record on basis of assetClassifier as alias
                            let oType = typology.value.find( (a) => {
                                return a.aliases.includes(assetClassifier);
                            })
                            // find returns undefined if the type on the data is not a known type
                            // this should never happen as classifer is set from mmMap which is derived from typology
                            // still guard against it
                            if(typeof oType != 'undefined') {
                                // data type is in typology list
                                aEachAssetPropSet['asset-type']         =   oType['atype']
                                aEachAssetPropSet['category']           =   oType['dep-category']
                                aEachAssetPropSet['audit-category']     =   oType['audit-cat']
                                bTypeInferenceComplete                  =   true
                            }
                            else {
                                // oType undefined - e.g. asset classifier is not in alias list
                                // fall thru with bTypeInferenceComplete = false
                            }
                        }
                        else {
                            // assetClassifier not resolved - attempt to use _CATEGORY                                                     
                            

                            if( (importedProps[ridx]?.[_CATEGORY] ?? "").length > 0) {    
                                // _CATEGORY on input data is set 

                                // destructure to use only 'aliases' part of typology object
                                let oType = typology.value.find( ({aliases}) => 
                                    aliases.includes(importedProps[ridx][_CATEGORY].toLowerCase()))
                                // find returns undefined if the type on the data is not a known type
                                if(typeof oType !== 'undefined') {
                                    // asset type is in typology list under an alias
                                    aEachAssetPropSet['asset-type']         =   oType['atype']
                                    aEachAssetPropSet['category']           =   oType['dep-category']
                                    aEachAssetPropSet['audit-category']     =   oType['audit-cat']
                                    bTypeInferenceComplete                  =   true              
                                }
                                else {
                                    // no classifier in alias list so fall thru with bTypeInferenceComplete = false
                                }
                            }
                            else {
                                // no category data - bTypeInferenceComplete remains false
                            }
                        }
                        if(!bTypeInferenceComplete )    {
                            // type still unresolved
 
                            // MANUFACTURER SINGLE CLASSIFIER DEFAULTS
                            // if manufacturer in the single classifer default file, set classifier accordingly
                            for( let i = 0; (i < mfgDefaults.value['mfg-defaults'].length); i++) { 
                                let defaultRegex = {}
                                try {
                                    defaultRegex = new RegExp(mfgDefaults.value['mfg-defaults'][i]?.regex?.replaceAll('/',''), 'i')
                                    if(defaultRegex.test(candidateMfg)) {
                                        // match on mfg regex
                                        assetClassifier                 =   mfgDefaults.value['mfg-defaults'][i].classifier 
                                        aEachAssetPropSet['mfg']        =   mfgDefaults.value['mfg-defaults'][i].cn
                                        aEachAssetPropSet['asset-type'] =   assetClassifier
                                        bMfgInferenceComplete           =   true
                                        bTypeInferenceComplete          =   true
                                        break
                                    }
                                }
                                catch(e)  {
                                    console.log("Bad Regex in mfgDefaults on index i: "+i)
                                    oRegexError.value.status        = true
                                    oRegexError.value.error         = "regex error in mfg default map"
                                    oRegexError.value['mfg-row']    = i
                                }
                            }
                        }

                        // set up defaults if we can't perform inference
                        // prefix field with '* ' then copy over original values
                        if(!bMfgInferenceComplete) {
                            stats.unresolvedMfgInputs++
                            aEachAssetPropSet['mfg']    =  "* " + (importedProps[ridx]?.[_MFG] ?? "").replace(/["]/g, '\\"')
                        }
                        if(!bModelInferenceComplete) {
                            stats.unresolvedModelInput++
                            aEachAssetPropSet['model']  =  "* " + (importedProps[ridx]?.[_MODEL] ?? "").replace(/["]/g, '\\"')
                        }
                        // default type assignment if not inferrable
                        if(!bTypeInferenceComplete) {
                            stats.notListedTypeRecords++
                            aEachAssetPropSet['asset-type']         =   'other, not otherwise listed'
                            aEachAssetPropSet['audit-category']     =   "Non-Config Item"
                            aEachAssetPropSet['dep-category']       =   "expensed"               
                        }

                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      RESOLVE CUSTODIAN FOR UNITS ISSUE
                        //      FabWeb (supposedly) requires custodians as mandatory prop,  so for inventory and unit 
                        //      assignments such as networking, a person is assigned to all unit issues.  This application 
                        //      provides for issuance of assets to units.  
                        //      If custodian is part of unit's 'listed-stewards' mark steward as unit ['short-name'] and 
                        //      steward-netid as 'unit-id'.
                        //
                        //      NOTE:  Actual personal assets such as laptops will be marked also - no way to distinquish
                        //      so manual intervention may be required to correct final stewardship.
                        //
                        if(!ISSUE_TESTING) {
                            
                            let sUnitAssignment = null
                            if(importedProps[ridx]?.[_CUSTODIAN] == "") {
                                // no custodian listed
                                stats.noCustodiansListed++
                                aEachAssetPropSet['steward']            =   "* not listed in data"
                                aEachAssetPropSet['steward-netid']      =   "*"
                                sUnitAssignment = '*'
                            }
                            else {
                
                                units.value.forEach( (u) => {
                                    if(u.hasOwnProperty('listed-stewards')) {
                                    
                                        if(u['listed-stewards'].includes(importedProps[ridx]?.[_CUSTODIAN])) {
                                            // steward listed is a unit steward.  Alter custodian to specified unit.
                                            // and set 'steward-netid' to 'unit-id' 
                                            //console.log("ListAssets:: importXLSX: FabWeb+: unit steward identified: "+importedProps[ridx][_CUSTODIAN]+" for unit "+u['short-name'])
                                            aEachAssetPropSet['steward']            =   u['short-name']
                                            aEachAssetPropSet['steward-netid']      =   u['unit-id']
                                            sUnitAssignment                         =   u['short-name']
                                            bHasCustodian                           =   true
                                            if(u['short-name'] != "inventory")
                                                aEachAssetPropSet['status']         =   "Active-Issued"
                                            else 
                                            aEachAssetPropSet['status']             =   "Active-Pending-Issue"
                                        }
                                    }   
                                })
                            }
                            // custodian is listed and not blank - resolve netid from graph
                            // Note:  an employee listed may have left university and thus not in AD and not accessible
                            //        with graph.  Add to failList if graph error.  Check failList on name prior to calling 
                            //        graph to reduce traffic.  (Some exited employees have multiple assets assigned)
                            //
                            if(sUnitAssignment == null) {
                                // keep FabWeb custodian as steward
                                if( !failList.value.includes(importedProps[ridx][_CUSTODIAN])) {
                                    // person is not in fail list so proceed with graph call
                                    await fetchEmplDataOnLegalName(importedProps[ridx][_CUSTODIAN])
                                    .then( () => {
                                        aEachAssetPropSet['steward']          =   importedProps[ridx][_CUSTODIAN]
                                        aEachAssetPropSet['steward-netid']    =   employeeEMResults.value.net_user_name
                                        aEachAssetPropSet['status']           =   "Active-Issued"
                                        bHasCustodian                         =     true   

                                        if(employeeEMResults.value.length == 0) {
                                            // not in EM table database
                                            stats.custodiansNotInAD++
                                            failList.value.push(importedProps[ridx][_CUSTODIAN])  
                                        }
                                    })
                                }  
                            }  
                        }
                        // FINAL CLEANUP
                        // If bHasCUstodian is false, then is just in inventory unissued
                        if(!bHasCustodian) {
                            aEachAssetPropSet['status']   =   "Active-Pending-Issue"
                        }
                        

                        //load into return array
                        assetProps.value.push(aEachAssetPropSet)    
                    } // end loop over input rows
                   
                }   // end conditional on valid format     
                else {
                    // invalid FABWeb+ format.  Return will show bBadFile true
                    console.log("useInferenceAPI:: Bad file format on submitted array")
                }  
                return assetProps.value;
                break;

            case 'FABWEB':               
                /*   column assignments for FabWeb data
                    [
                        "Responsible Organization Code"         --> 6-digit orgcode
                        "Primary Tag Perm"                      --> PTAG
                        "Permanent Tag Ptag"                    --> PTAG
                                First digit identifies the asset as a legacy record converted from 
                                UFAS-PAS (L=Primary record or S=Component record) or an asset entered 
                                directly into the Banner record (P).
                        "Originating Tag Otag"                  --> OTAG
                        "Barcode Nbr"                           --> baracode
                        "Asset Description"                     --> description -  parsed for mfg/model/type
                        "Commodity Code"                        --> (typically miscategorized)
                        "Custodian Name"                        --> maps to steward
                        "Equipment Manager Name"                --> not mapped      
                        "Responsible Location Code"             --> maps to Location"  -->  location-code
                                In addition, off-campus location codes include:
                                1Z000 UIC Buildings – Other
                                109990 5150 00000000 Off-Site Location
                                109995 5200 00000000 Off-Site Employee
                                2Z000 UIC Buildings – Other
                                209990 5150 00000000 Off-Site Location
                                209995 5200 00000000 Off-Site Employee
                                4Z000 UIS Buildings – Other
                                409990 5150 0000000 Off-Site Location
                                409995 5200 0000000 Off-Site Location
                        "Responsible Location Title"            --> maps to location-detail
                                Format::   'bldg-no' _ '8-digit-room-no-lead-zero' _ 'bldg-name'
                        "Entities Attribute Value"              --> not mapped
                                Format:: (self supporting (4e) activity where used)
                                Used to identify/distinguish the various revenue-generating activities. 
                                Each item of equipment currently associated with such activity must have 
                                this code assigned.
                                Valid Entity Codes crosswalk to the Fund Code hierarchy level 3 code:

                                    Blank or 00 – non-revenue-generating activity
                                    Storerooms and Service Departments
                                    29 – UI2 COP Funding
                                >>> 30 – Communication and Computing Services – Level 3 Fund Code 3100
                                    31 – Plant and Service Operations – Level 3 Fund Code 3110
                                    Auxiliary Enterprises Not Under Indenture
                                    40 – Student/Staff Programs and Services – Level 3 Fund Code 3200
                                    Auxiliary Enterprises Under Indenture
                                    50 – Campus Life - Housing and Food Service – Level 3 Fund Code 3300
                                    51 – Campus Life - Student Activity Facilities – Level 3 Fund Code 3310
                                    52 – Campus Life - Parking – Level 3 Fund Code 3320
                                    Activities
                                    60 – Instructional Course Activities – Level 3 Fund Code 3400
                                    61 – Professional Development Activities – Level 3 Fund Code 3410
                                    62 – Unique Instructional Programs – Level 3 Fund Code 3420
                                    63 – Agricultural Operations – Level 3 Fund Code 3430
                                    64 – Commercial Operations – Not Under Indenture – Level 3 Fund Code 3600
                                    65 – Commercial Operations – Under Indenture – Level 3 Fund Code 3610
                                    66 – Hospital and Clinics – Level 3 Fund Code 3500
                                    67 – Public Service and Academic Support Activities – Level 3 Fund Code 3440
                                    68 – Intercollegiate Athletics – Level 3 Fund Code 3450
                        "Entitles Attribute Short Desc"         --> not mapped
                        "Manufacturer"                          --> manufacturer
                        "Model"                                 --> model
                                    Model number assigned by manufacturer. Use NA when no model number exists.
                        "Acquisition Date"                      --> maps to purchase-date
                        "Asset Condition Code"                  --> not mapped - see OBFS table of CCs
                                Values: 
                                    A = Working Available to Other Departments
                                    B = Working available on site only
                                    E = Working Loaned to faculty, staff or students
                                    L = Working Loaned to other University
                                    N = Working Not in use/Not available
                                    P = Working Pending resolution
                                    S = Working Surplus-Internal Transfer
                                    U = Working In Use/Unavailable for transf
                        "Asset Type Code"                       --> not mapped - see OBFS table of type codes
                        "Title to Code"                         --> not mapped - see OBFS table of titles
                                    Identifies who holds title to (owns) the equipment.
                                    E = Endowment Farms
                                    F = Federally owned
                                    L = Operating Lease/Rented/Loaned
                                    O = Other Sponsor Titled
                                    P = Privately Owned - to follow staff member
                                >>> S = State Titled
                        "Serial Number -- Vin"                  --> mapped to serial-number
                        "Part Nbr Vehicle Tag"                  --> not mapped
                        "Fund Src Amount"                       --> not mapped
                        "Primary Sum"                           --> mapped to purchase-price
                    ]
                */     

                // header key indexing
                let _FW_ORG                =   0
                let _FW_PTAG_PRI           =   1
                let _FW_PTAG_PERM          =   2
                let _FW_OTAG               =   3
                let _FW_BARCODE            =   4
                let _FW_DESC               =   5
                let _FW_COMMODITY_CODE     =   6
                let _FW_COMMODITY_DESC     =   7
                let _FW_CUSTODIAN_NAME     =   8
                let _FW_EQUIP_MGR_NAME     =   9
                let _FW_LOCATION_CODE      =   10
                let _FW_LOCATION_TITLE     =   11
                let _FW_ENTITIES           =   12
                let _FW_ENTITIES_DESC      =   13
                let _FW_MFG                =   14
                let _FW_MODEL              =   15
                let _FW_ACQUISITION_DATE   =   16
                let _FW_CONDITION_CODE     =   17
                let _FW_TYPE_CODE          =   18
                let _FW_TITLE_TO           =   19
                let _FW_SERIAL_NUMBER      =   20
                let _FW_PART_NBR           =   21
                let _FW_FUND_SRC_$         =   22
                let _FW_PRIMARY_SUM_$      =   23

                // check to see if row 0 conforms to the above structure
                /*
                if(importedProps[0][_FW_ORG].toLowerCase() != 'responsible organization code')             bBadFile.value = true
                if(importedProps[0][_FW_PTAG_PRI].toLowerCase() != 'primarytag perm')                      bBadFile.value = true
                if(importedProps[0][_FW_PTAG_PERM].toLowerCase() != 'permanent tag ptag')                  bBadFile.value = true
                if(importedProps[0][_FW_OTAG].toLowerCase() != 'originating tag otag')                     bBadFile.value = true
                if(importedProps[0][_FW_BARCODE].toLowerCase() != 'barcode nbr')                           bBadFile.value = true
                if(importedProps[0][_FW_DESC].toLowerCase() != 'asset description')                        bBadFile.value = true
                if(importedProps[0][_FW_COMMODITY_CODE].toLowerCase() != 'commodity code')                 bBadFile.value = true
                if(importedProps[0][_FW_COMMODITY_DESC].toLowerCase() != 'commodity description')          bBadFile.value = true
                if(importedProps[0][_FW_CUSTODIAN_NAME].toLowerCase() != 'custodian name')                 bBadFile.value = true
                if(importedProps[0][_FW_EQUIP_MGR_NAME].toLowerCase() != 'equipment manager name')         bBadFile.value = true
                if(importedProps[0][_FW_LOCATION_CODE].toLowerCase() != 'responsible location code')       bBadFile.value = true
                if(importedProps[0][_FW_LOCATION_TITLE].toLowerCase() != 'responsible location title')     bBadFile.value = true
                if(importedProps[0][_FW_ENTITIES].toLowerCase() != 'entities attribute value')             bBadFile.value = true
                if(importedProps[0][_FW_ENTITIES_DESC].toLowerCase() != 'entitles attribute short desc')   bBadFile.value = true
                if(importedProps[0][_FW_MFG].toLowerCase() != 'manufacturer')                              bBadFile.value = true
                if(importedProps[0][_FW_MODEL].toLowerCase() != 'model')                                   bBadFile.value = true
                if(importedProps[0][_FW_ACQUISITION_DATE].toLowerCase() != 'acquisition date')             bBadFile.value = true
                if(importedProps[0][_FW_CONDITION_CODE].toLowerCase() != 'asset condition code')           bBadFile.value = true
                if(importedProps[0][_FW_TYPE_CODE].toLowerCase() != 'asset type code')                     bBadFile.value = true
                if(importedProps[0][_FW_TITLE_TO].toLowerCase() != 'title to code')                        bBadFile.value = true
                if(importedProps[0][_FW_SERIAL_NUMBER].toLowerCase() != 'serial number -- vin')            bBadFile.value = true
                if(importedProps[0][_FW_PART_NBR].toLowerCase() != 'part nbr vehicle tag')                 bBadFile.value = true
                if(importedProps[0][_FW_FUND_SRC_$].toLowerCase() != 'fund src  amount')                   bBadFile.value = true
                if(importedProps[0][_FW_PRIMARY_SUM_$].toLowerCase() != 'primarysum')                      bBadFile.value = true
              
                */
                // FABWEB exports have consistent format
                // adjust indicies accordingly
                let _FW_HDR_ROW         =   0
                let _FW_START_OF_DATA   =   1
 
                if( bBadFile.value == false) {
                    // in FABWEB+ format, so proceed
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    //
                    //      LOOP OVER DATA INPUT ROWS
                    //
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    ridxFWLoop: for( let ridx = _FW_START_OF_DATA; ridx < importedProps.length; ridx++) {

                        // asset property storage object (to be written to database)
                        let aEachAssetPropSet   =   {}
                                                
                        // starting conditions
                        bMfgInferenceComplete   =   false
                        bModelInferenceComplete =   false
                        bTypeInferenceComplete  =   false
                        let assetFWClassifier   =   ""
                        stats.inputRecords++
 
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      Set owning org  Check to see if in proper format
                        //      Note: Some orgcodes are in spreadsheet as integer
                        //
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        try {
                            let checkFWCode = (importedProps[ridx]?.[_FW_ORG]).toString()
                            if( checkFWCode.length != 6) throw new Error("orgcode not in proper 6 digit format")
                            aEachAssetPropSet['owner-org'] = checkFWCode.substring(0,3)
                        }
                        catch(e) {
                            console.log ("useInferenceAPI:: Data error: "+e)
                            // skip this record
                            stats.badOrgcodeRecords++
                            continue ridxFWLoop
                        }
                        
                        
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      GENERAL PROCESS FOR MANIPULATION OF DATA ELEMENTS
                        //
                        //      Uses asset-typology:
                        //          'atype'             asset-type value
                        //          'dep-category'      depreciation term
                        //          'audit-cat'         State audit category
                        //          'aliases'           Array of strings used to infer type
                        //
                        //      1. Sanitize _DESC field for special characters that will fubar SQL insert.  Sanitize
                        //         _ITEM_ID for proper format.  Improper tags are rewritten to 12 char plus 'truncated'
                        //         to preserve data.
                        //      2. Copy pass thru props that don't depend on type/mfg/model/description
                        //      3. Attempt to infer asset type from mfg, model, or description.  On regex matches with
                        //         model number, set asset classifier
                        //      4. If cannot parse model from _MODEL field attempt to do so on _DESC field. Set asset classifier
                        //      5. If asset classifier is not set (== 'unresolved'), attempt to determine classifier from _CATEGORY field.
                        //         Note:  Many _CATEGORY fields are blank ("").  Skip function if blank.
                        //      6. If asset classifier is set (!= 'unresolved'), use classifier to look up on aAssetTopology structure
                        //         and set asset-type, audit-cat, and dep(reciation)-category.
                        //      6a. If not set, then use defaults
                        //      7. If 'custodian' is set, determine whether custodian is listed as unit 
                        //         steward, and if so set 'steward' to unit 'short-name' and 'steward-netid'
                        //         to 'unit-id'
                        //      8. If 'custodian' is not in unit-steward list, set 'steward' to 'custodian'band
                        //         perform graph lookup on'custodian' to extract netid.
                        //      9. If mfg or models are not determined from process, prefix with * and copy data as 
                        //         specified on input
                        //      10. Store prop array as asset and reiterate next row  
                        //
                        //>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                        // sanitize the description e.g. '20" Monitors' so as to allow SQL store to work
                        let sanFWDesc = (importedProps[ridx]?.[_FW_DESC] ??' ').replace(/["]/g, '\\"')
                        aEachAssetPropSet['description']        =   sanFWDesc

                        // Eliminate any accounting records
                        let finFWRegex = {}
                        for(let f = 0; f < finExcissions.value.length; f++) {
                            try {
                                finFWRegex = new RegExp(finExcissions.value[f], 'i')
                                // continues only if valid regex otherwise catch block executed
                                if(finFWRegex.test(sanFWDesc)) {
                                    // regex match
                                    stats.finRecords++
                                    // ignore record and restart input record processing
                                    continue ridxFWLoop
                                } 
                            }
                            catch(e) {
                                console.log("Bad Regex in finExcissions on indicies i,j: "+f)
                                oRegexError.value.status        = true
                                oRegexError.value.error         = "regex error in financial recissions map"
                                oRegexError.value['mfg-row']    = f
                            }               
                        }
                        

                        // sanitize and test _ITEM_ID.  Some ids are abnormal.  Database has field length 32 chars
                        // and in excess will throw errors.  Limit to 12 plus words 'truncated' and continue
                        try {
                            let tagLength = importedProps[ridx]?.[_FW_PTAG_PERM]?.length ?? "tag undef"
                            if(tagLength > 32 || typeof tagLength == 'undefined') throw Error("tag length: "+tagLength)
                            aEachAssetPropSet['tag']                =   importedProps[ridx][_FW_PTAG_PERM]
                        }
                        catch(e) {
                            console.log("useInferenceAPI:: failed tag length test: "+e)
                            stats.badItemIDLengthRecords++
                            aEachAssetPropSet['tag'] =   importedProps[ridx][_FW_PTAG_PERM].substring(0,12)+"_truncated"
                        }

                        // pass thrus - these props do not depend on asset-type, mfg, or model                       
                        aEachAssetPropSet['otag']               =   importedProps[ridx][_FW_OTAG]
                        aEachAssetPropSet['location']           =   importedProps[ridx][_FW_LOCATION_CODE]
                        aEachAssetPropSet['location-detail']    =   importedProps[ridx][_FW_LOCATION_TITLE]
                        aEachAssetPropSet['serial-no']          =   importedProps[ridx][_FW_SERIAL_NUMBER]
                        // invoice number bnot stored in FabWeb
                        aEachAssetPropSet['invoice-number']     =   ""
                        aEachAssetPropSet['purchase-date']      =   importedProps[ridx][_FW_ACQUISITION_DATE]
                        
                        // if cost is null, reported as "" on import, reset to 0.00 float to satisfy SQL
                        // database table description (cost specified as float) to avoid insert error
                        if(importedProps[ridx][_FW_PRIMARY_SUM_$] == "")    aEachAssetPropSet['cost']  =   0.00
                        else                                                aEachAssetPropSet['cost']  =   importedProps[ridx][_FW_PRIMARY_SUM_$]

                        // Set source to FabWeb                          
                        aEachAssetPropSet['source-code']  =   'FABWEB'
                        
                      
                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      INFERENCE PROCESS FOR DETERMINING MANUFACTURER/MODEL 
                        //
                        //      Attempt to match up a model.  The first index iterates the map records and the
                        //      the second index (j) iterates the model regex.  If the model inference fails on 
                        //      the first index, apply the regex to the manufacturer field for a partial solution. 
                        //      This will not give an asset type.
                        // 
                        //      Note:  I added the try/catch block to the regex generation because the mfgModelMap
                        //             is a constant source of errors (regex errors as items are added).   The catch block
                        //             will emit a console log on the map indices and also pass a ref value back to the 
                        //             calling routine.   This allows the calling routine to flag the errors for the user.
                        //
                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                        // guard against null prop on imported data on _MFG and _MODEL columns
                        let candidateFWMfg    = (importedProps[ridx]?.[_FW_MFG] ?? "na").toLowerCase().trim()
                        let candidateFWModel  = (importedProps[ridx]?.[_FW_MODEL] ?? "na").toLowerCase().trim()
                        
                        //  some model entries are blank or marked N/A or NA - speed up by avoiding these iterations
                        if(     (candidateFWModel.length == 0)
                            ||   candidateFWModel.toLowerCase().includes('n/a')
                            ||   candidateFWModel.toLowerCase().includes('na') ) {
                            
                            stats.modelInputEmpty++
                            // drop through with inference incomplete to description inference.
                        }
                        else  {
                        // iterate over map (src/configuration/mfgModelMap.json)
                            mmFWInference: for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) { 
                                for( let j = 0; (j < mfgModelMap.value['mm-map'][i].models.length); j++) {               

                                    let modelFWRegex = {}
                                    try {
                                        modelFWRegex = new RegExp(mfgModelMap.value['mm-map'][i]['models'][j].regex, 'i')
                                        // continues only if valid regex otherwise catch block executed
                                        if(modelFWRegex.test(candidateFWModel)) {
                                            // regex match
                                            aEachAssetPropSet['model']  =   mfgModelMap.value['mm-map'][i].models[j].cmm
                                            aEachAssetPropSet['mfg']    =   mfgModelMap.value['mm-map'][i].cn
                                            assetFWClassifier           =   mfgModelMap.value['mm-map'][i].models[j].classifier   
                                            bModelInferenceComplete     =   true                                             
                                            bMfgInferenceComplete       =   true    
                                            stats.resolvedModelInference++

                                            // break iteration inner and outer since we have a solution
                                            break mmFWInference
                                        } 
                                    }
                                    catch(e) {
                                        console.log("Bad Regex in mfgModelMap (MM) on indicies i,j: "+i+", "+j)
                                        oRegexError.value.status        = true
                                        oRegexError.value.error         = "regex error in model map"
                                        oRegexError.value['mfg-row']    = i
                                        oRegexError.value['model-row']  = j
                                    }                                
                                }   // end model iteration   
                            }       // end on mmap record mfg iteration
                        }

                        // if model or mfg not found (inferenceComplete)
                        // attempt inference on description
                        if(!bMfgInferenceComplete || !bModelInferenceComplete)    {
                            let aFWDesc  = (importedProps[ridx]?.[_FW_DESC] ?? "").toLowerCase().trim()
                            // apply regex to  description
                            mapFWOLoop: for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) {   
                                for( let j =0; (j < mfgModelMap.value['mm-map'][i].models.length); j++) {
                                    
                                    let descFWRegex = {}
                                    // try/catch to catch possibly bad regex in maps
                                    try {
                                        descFWRegex = new RegExp(mfgModelMap.value['mm-map'][i]['models'][j].regex, 'i')
 
                                        if(descFWRegex.test(aFWDesc)) {
                                            // regex matched model in description
                                            aEachAssetPropSet['mfg']    =  mfgModelMap.value['mm-map'][i].cn 
                                            aEachAssetPropSet['model']  =  mfgModelMap.value['mm-map'][i].models[j].cmm       
                                            assetFWClassifier           =  mfgModelMap.value['mm-map'][i].models[j].classifier 
                                            bModelInferenceComplete     =   true                                         
                                            bMfgInferenceComplete       =   true 
                                            
                                            // exit as we have a solution
                                            break mapFWOLoop
                                        }   
                                    }
                                    catch(e) {
                                        console.log("Bad Regex in mfgModelMap (DESC) on indicies i,j: "+i+", "+j)
                                        oRegexError.value.status        = true
                                        oRegexError.value.error         = "regex error in model map"
                                        oRegexError.value['mfg-row']    = i
                                        oRegexError.value['model-row']  = j
                                    }                                                                               
                                }    
                            }   
                        }                         
                        
                        // consider possibility that the model is unknown but the manufacturer listed is approximately
                        // valid.   Iterate manufacturers on map and if matched set to cannonical mfg name
                        // Since there is no model, can't resolve asset classifier
                        if (!bMfgInferenceComplete) {
                            for( let i = 0; (i < mfgModelMap.value['mm-map'].length); i++) { 

                                let mfgFWRegex = {}
                                try {
                                    mfgFWRegex = new RegExp(mfgModelMap.value['mm-map'][i]?.regex?.replaceAll('/',''), 'i')
                                    if(mfgFWRegex.test(candidateFWMfg)) {
                                        // match on mfg regex
                                        aEachAssetPropSet['mfg']    =   mfgModelMap.value['mm-map'][i].cn
                                        bMfgInferenceComplete       =   true
                                        break
                                    }
                                }
                                catch(e) {
                                    console.log("Bad Regex in mfgModelMap (MFG) on index: i: "+i)
                                    oRegexError.value.status        = true
                                    oRegexError.value.error         = "regex error in mfg map"
                                    oRegexError.value['mfg-row']    = i
                                    oRegexError.value['model-row']  =   null
                                }
                            }   // end map loop on mfg
                        }

                        // set up asset type based on assetClassifier
                        // - if the classifier has been set (len > 0) then use the classifier to index typology
                        //   aliases to obtain the asset-type.
                        // - if the classifier is not set, attempt to parse the _CATEGORY field.
                        //   This field is poorly maintained and can be blank ("")
                        //   match against typology aliases to get asset-type from atype key
                        // - if this fails, deault the type to unknown.
                        //
                        if (assetFWClassifier !== "" )    {

                             // have classifier so lookup typology record on basis of assetClassifier as alias
                            let oType = typology.value.find( (a) => {
                                return a.aliases.includes(assetFWClassifier);
                            })
 
                            // find returns undefined if the type on the data is not a known type
                            // this should never happen as classifer is set from mmMap which is derived from typology
                            // still guard against it
                            if(typeof oType != 'undefined') {
                                // data type is in typology list
                                aEachAssetPropSet['asset-type']         =   oType['atype']
                                aEachAssetPropSet['category']           =   oType['dep-category']
                                aEachAssetPropSet['audit-category']     =   oType['audit-cat']
                                bTypeInferenceComplete                  =   true
                            }
                            else {
                                // oType undefined - e.g. asset classifier is not in alias list
                                // fall thru with bTypeInferenceComplete = false
                            }
                        }
                        else {
                            // assetClassifier not resolved - attempt to use _CATEGORY                                                     
                            if( (importedProps[ridx]?.[_FW_COMMODITY_CODE] ?? "").length > 0) {    
                                // _CATEGORY on input data is set 

                                // destructure to use only 'aliases' part of typology object
                                let oType = typology.value.find( ({aliases}) => 
                                    aliases.includes(importedProps[ridx][_FW_COMMODITY_CODE].toLowerCase()))
                                // find returns undefined if the type on the data is not a known type
                                if(typeof oType !== 'undefined') {
                                    // asset type is in typology list under an alias
                                    aEachAssetPropSet['asset-type']         =   oType['atype']
                                    aEachAssetPropSet['category']           =   oType['dep-category']
                                    aEachAssetPropSet['audit-category']     =   oType['audit-cat']
                                    bTypeInferenceComplete                  =   true   
                                }
                                else {
                                    // no classifier in alias list so fall thru with bTypeInferenceComplete = false
                                }
                            }
                            else {
                                // no category data - bTypeInferenceComplete remains false
                            }
                        }
                        if(!bTypeInferenceComplete )    {
                            // type still unresolved
 
                            // MANUFACTURER SINGLE CLASSIFIER DEFAULTS
                            // if manufacturer in the single classifer default file, set classifier accordingly
                            for( let i = 0; (i < mfgDefaults.value['mfg-defaults'].length); i++) { 
                                let defaultFWRegex = {}
                                try {
                                    defaultFWRegex = new RegExp(mfgDefaults.value['mfg-defaults'][i]?.regex?.replaceAll('/',''), 'i')
                                    if(defaultFWRegex.test(candidateFWMfg)) {
                                        // match on mfg regex
                                        assetFWClassifier                 =   mfgDefaults.value['mfg-defaults'][i].classifier 
                                        aEachAssetPropSet['mfg']        =   mfgDefaults.value['mfg-defaults'][i].cn
                                        aEachAssetPropSet['asset-type'] =   assetFWClassifier
                                        bMfgInferenceComplete           =   true
                                        bTypeInferenceComplete          =   true
                                        break
                                    }
                                }
                                catch(e)  {
                                    console.log("Bad Regex in mfgDefaults on index i: "+i)
                                    oRegexError.value.status        = true
                                    oRegexError.value.error         = "regex error in mfg default map"
                                    oRegexError.value['mfg-row']    = i
                                }
                            }
                        }

                        // set up defaults if we can't perform inference
                        // prefix field with '* ' then copy over original values
                        if(!bMfgInferenceComplete) {
                            stats.unresolvedMfgInputs++
                            aEachAssetPropSet['mfg']    =  "* " + (importedProps[ridx]?.[_FW_MFG] ?? "").replace(/["]/g, '\\"')
                        }
                        if(!bModelInferenceComplete) {
                            stats.unresolvedModelInput++
                            aEachAssetPropSet['model']  =  "* " + (importedProps[ridx]?.[_FW_MODEL] ?? "").replace(/["]/g, '\\"')
                        }
                        // default type assignment if not inferrable
                        if(!bTypeInferenceComplete) {
                            stats.notListedTypeRecords++
                            aEachAssetPropSet['asset-type']         =   'other, not otherwise listed'
                            aEachAssetPropSet['audit-category']     =   "Non-Config Item"
                            aEachAssetPropSet['dep-category']       =   "expensed"   
                        }

                        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                        //
                        //      RESOLVE CUSTODIAN FOR UNITS ISSUE
                        //      FabWeb (supposedly) requires custodians as mandatory prop,  so for inventory and unit 
                        //      assignments such as networking, a person is assigned to all unit issues.  This application 
                        //      provides for issuance of assets to units.  
                        //      If custodian is part of unit's 'listed-stewards' mark steward as unit ['short-name'] and 
                        //      steward-netid as 'unit-id'.
                        //
                        //      NOTE:  Actual personal assets such as laptops will be marked also - no way to distinquish
                        //      so manual intervention may be required to correct final stewardship.
                        //
                        if(!ISSUE_TESTING) {
                            let sUnitAssignment = null
                            if(importedProps[ridx]?.[_FW_CUSTODIAN_NAME].length == 0) {
                                // no custodian listed
                                stats.noCustodiansListed++
                                aEachAssetPropSet['steward']            =   "* not listed in data"
                                aEachAssetPropSet['steward-netid']      =   "*"
                                sUnitAssignment = '*'
                            }
                            else {
                                units.value.forEach( (u) => {
                                    if(u.hasOwnProperty('listed-stewards')) {
                                    
                                        if(u['listed-stewards'].includes(importedProps[ridx]?.[_FW_CUSTODIAN_NAME])) {
                                            // steward listed is a unit steward.  Alter custodian to specified unit.
                                            // and set 'steward-netid' to 'unit-id' 
                                            aEachAssetPropSet['steward']            =   u['short-name']
                                            aEachAssetPropSet['steward-netid']      =   u['unit-id']
                                            sUnitAssignment                         =   u['short-name']
                                            bHasCustodian                           =   true
                                            if(u['short-name'] != "inventory")
                                                aEachAssetPropSet['status']         =   "Active-Issued"
                                            else 
                                            aEachAssetPropSet['status']             =   "Active-Pending-Issue"
                                        }
                                    }   
                                })
                            }
                            // custodian is listed and not blank - resolve netid from graph
                            // Note:  an employee listed may have left university and thus not in AD and not accessible
                            //        with graph.  Add to failList if graph error.  Check failList on name prior to calling 
                            //        graph to reduce traffic.  (Some exited employees have multiple assets assigned)
                            //
                            if(sUnitAssignment == null) {
                                // keep FabWeb custodian as steward                      
                                if( !failList.value.includes(importedProps[ridx][_FW_CUSTODIAN_NAME])) {
                                    // person is not in fail list so proceed with ArchiBus EM call
                                    // 
                                    await fetchEmplDataOnLegalName(importedProps[ridx][_FW_CUSTODIAN_NAME])
                                    .then( () => {
                                        aEachAssetPropSet['steward']          =   importedProps[ridx][_FW_CUSTODIAN_NAME]
                                        aEachAssetPropSet['steward-netid']    =   employeeEMResults.value.net_user_name
                                        aEachAssetPropSet['status']           =   "Active-Issued"
                                        bHasCustodian                         =     true   

                                        if(employeeEMResults.value.length == 0) {
                                            // not in EM table database
                                            stats.custodiansNotInAD++
                                            failList.value.push(importedProps[ridx][_FW_CUSTODIAN_NAME])  
                                        }
                                    })
                                }  
                            }  
                        }  // end special testing code excision
 
                        // FINAL CLEANUP
                        // If bHasCUstodian is false, then is just in inventory unissued
                        if(!bHasCustodian) {
                            aEachAssetPropSet['status']   =   "Active-Pending-Issue"
                        }
                        

                        //load into return array
                        assetProps.value.push(aEachAssetPropSet)    
                    } // end loop over input rows
                   
                }   // end conditional on valid format     
                else {
                    // invalid FABWeb+ format.  Return will show bBadFile true
                    console.log("useInferenceAPI:: Bad file format on submitted array")
                }  
                return assetProps.value;

                break;        

            case 'OTHER':
                // OTHER XLSX INPORT
                // column names pulled to generate keys for store
                // Note that keys MUST be in API dictionary for assets
                //
                //console.log("ListAssets:: importXLSX: Other Excel: aoaData: "+JSON.stringify(rawAAData))     

                // skip header row and iterate over import spreadsheet rows
                for( let ridx = 1; ridx < importedProps.length; ridx++) {
                    let aEachAssetPropSet = {}
                    for( let pidx = 0; pidx < rawAAData[ridx].length; pidx++) {
                        // iterate the colums assigning props as key:value pairs
                        aEachAssetPropSet[importedProps[0][pidx]] = importedProps[ridx][pidx]
                    }
                    await storeAsset(aEachAssetPropSet)
                    .then( () => {
                        //console.log("ListAssets:: importXLSX: Other Excel: aAsset: "+JSON.stringify(aAssetProps))
                        console.log("Storing assets on pkey: "+assetKeyData.value[0].pkey)   
                    })
                }
                break;
            default:
                break;
        }       // end switch on source type
    }           // end function

 
    return {  assetProps, bBadFile, oRegexError, fixupAssetProps }
}

export default useInferenceAPI